'use client';

import { useState } from 'react';
import { formatDateShortYear } from '@/lib/date-format';

interface PathwayData {
  id: string;
  nameId: string;
  nameEn: string;
  fullNameId: string;
  fullNameEn: string;
  descriptionId: string;
  descriptionEn: string;
  criteriaId: string;
  criteriaEn: string;
  costId: string;
  costEn: string;
  selectionRatio?: string;
  registrationStart?: string;
  registrationEnd?: string;
  resultsDate?: string;
  prosId: string[];
  prosEn: string[];
  consId: string[];
  consEn: string[];
  suitabilityId: string;
  suitabilityEn: string;
  linkSlug: string;
}

interface PathwayComparisonProps {
  pathways: PathwayData[];
  lang: string;
}

export function PathwayComparison({ pathways, lang }: PathwayComparisonProps) {
  const isId = lang === 'id';
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-card p-4 md:p-6">
      <h2 className="font-heading text-lg font-bold text-text-primary">
        {isId ? 'Perbandingan Jalur Masuk PTN' : 'University Admission Pathway Comparison'}
      </h2>
      <p className="mt-1 text-sm text-text-secondary">
        {isId ? 'Bandingkan jalur SNBP, SNBT, dan Mandiri untuk memilih yang paling sesuai.' : 'Compare SNBP, SNBT, and Mandiri pathways to find the best fit.'}
      </p>

      {/* Comparison cards */}
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {pathways.map(pw => {
          const isExpanded = expanded === pw.id;
          return (
            <div
              key={pw.id}
              className="rounded-xl border border-border-subtle bg-surface-root p-4 transition-shadow hover:shadow-md"
            >
              <h3 className="text-base font-bold text-text-primary">
                {isId ? pw.nameId : pw.nameEn}
              </h3>
              <p className="mt-0.5 text-xs text-text-tertiary">
                {isId ? pw.fullNameId : pw.fullNameEn}
              </p>
              <p className="mt-2 text-sm text-text-secondary">
                {isId ? pw.descriptionId : pw.descriptionEn}
              </p>

              {/* Quick stats */}
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-secondary">{isId ? 'Biaya' : 'Cost'}</span>
                  <span className="font-medium text-text-primary">{isId ? pw.costId : pw.costEn}</span>
                </div>
                {pw.selectionRatio && (
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{isId ? 'Kuota' : 'Quota'}</span>
                    <span className="font-medium text-text-primary">{pw.selectionRatio}</span>
                  </div>
                )}
                {pw.registrationStart && pw.registrationEnd && (
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{isId ? 'Pendaftaran' : 'Registration'}</span>
                    <span className="font-medium text-text-primary text-right text-xs">
                      {formatDateShortYear(pw.registrationStart, lang)} – {formatDateShortYear(pw.registrationEnd, lang)}
                    </span>
                  </div>
                )}
                {pw.resultsDate && (
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{isId ? 'Pengumuman' : 'Results'}</span>
                    <span className="font-medium text-text-primary">{formatDateShortYear(pw.resultsDate, lang)}</span>
                  </div>
                )}
              </div>

              {/* Expand button */}
              <button
                onClick={() => setExpanded(isExpanded ? null : pw.id)}
                className="mt-3 w-full rounded-lg border border-border-subtle px-3 py-2 text-sm font-medium text-accent-primary hover:bg-surface-hover transition-colors focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2"
              >
                {isExpanded
                  ? (isId ? 'Sembunyikan detail' : 'Hide details')
                  : (isId ? 'Lihat detail' : 'View details')}
              </button>

              {/* Expanded details */}
              {isExpanded && (
                <div className="mt-3 space-y-3 border-t border-border-subtle pt-3">
                  <div>
                    <p className="text-xs font-semibold text-text-secondary uppercase">{isId ? 'Kriteria' : 'Criteria'}</p>
                    <p className="mt-1 text-sm text-text-primary">{isId ? pw.criteriaId : pw.criteriaEn}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-semantic-success uppercase">
                      {isId ? 'Kelebihan' : 'Pros'}
                    </p>
                    <ul className="mt-1 space-y-1">
                      {(isId ? pw.prosId : pw.prosEn).map((pro, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-sm text-text-primary">
                          <span className="text-semantic-success shrink-0">+</span>
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-semantic-error uppercase">
                      {isId ? 'Kekurangan' : 'Cons'}
                    </p>
                    <ul className="mt-1 space-y-1">
                      {(isId ? pw.consId : pw.consEn).map((con, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-sm text-text-primary">
                          <span className="text-semantic-error shrink-0">−</span>
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="panel-info rounded-lg p-2">
                    <p className="text-xs font-semibold text-accent-primary">
                      {isId ? 'Cocok untuk' : 'Best suited for'}
                    </p>
                    <p className="mt-0.5 text-sm text-text-primary">
                      {isId ? pw.suitabilityId : pw.suitabilityEn}
                    </p>
                  </div>
                  <a
                    href={`/${pw.linkSlug}`}
                    className="inline-block text-sm font-medium text-accent-primary hover:underline"
                  >
                    {isId ? 'Lihat jadwal lengkap' : 'View full schedule'} →
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
